<template>
  <div style="float: left">
    <Toolbar class="p-0">
      <template #start>
        <MultiSelect v-model="currentColumns" class="m-1" placeholder="" :options="columns" option-label="header"
          option-value="field" append-to="body" @change="changeColumn($event)">
          <template #value>
            Колонки
          </template>
        </MultiSelect>
        <Checkbox id="cb_property_show" v-model="isSecondPropertyShow" binary class="ml-4" />
        <label for="cb_property_show" class="ml-2" style="margin-bottom: 1px"
          @click="isSecondPropertyShow = !isSecondPropertyShow">Показать все свойства</label>      
      </template>
    </Toolbar>

    <CustomDataTable v-model:selectedRow="selection" :data="propertyFit" :columns="columnsFit"
      :scroll-height="scrollHeight" @dblclick="onDblClick" />
  </div>

  <Dialog v-model:visible="showWriteDialog" :style="{ width: '560px' }" modal>
    <template #header>
      <i class="pi pi-exclamation-circle mr-3" style="font-size: 1.5rem; color: rgb(204,204,0)" />
      <div class="col-10 font-bold text-center" style="font-size: 1.4rem">
        Задать значение
      </div>
    </template>

    <div class="confirmation-content mt-1">
      <div class="text-center">
        <span style="font-size: 1rem">
          {{ `${selection.sysName} (${selection.typeName}) фл.${selection.flangeNo}: ` }}
          <InputText v-model="propertyValue" autofocus type="number" />
        </span>
      </div>
    </div>
    <template #footer>
      <Button label="Нет" icon="pi pi-times" class="p-button-text" @click="showWriteDialog = false" />
      <Button label="Ok" icon="pi pi-check" class="p-button-text" @click="writeValue()" />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { usePropertyUtils } from './composables/usePropertyUtils';
import { useComponentUtils } from './composables/useComponentUtils';

const props = defineProps({
  property: {
    type: Array,
    default: () => [],
  },
  secondProperty: {
    type: Array,
    default: () => [],
  },
  columnVisible: {
    type: Array,
    default: () => [],
  },
  secondPropertyShow: {
    type: Boolean,
    default: false,
  },
  scrollHeight: {
    type: String,
    default: '400px',
  },
  tableName: {
    type: String,
    default: 'PropertiesForm',
  },
});

const store = useStore();
const { propertyWrite } = usePropertyUtils();
const { prepareURIData } = useComponentUtils();

const currentColumns = ref([]);
const qualityService = ref(null);
const selection = ref(null);
const propertyValue = ref(null);
const showWriteDialog = ref(false);
const isSecondPropertyShow = ref(props.secondPropertyShow);

const columns = ref([
  {
    header: 'PID',
    field: 'id',
    sortable: true,
    bodyStyle: 'text-align: right;',
  },
  {
    header: 'Состояние',
    field: 'active',
    sortable: true,
  },
  {
    header: 'Фл',
    field: 'flangeNo',
    sortable: true,
    bodyStyle: 'text-align: center;',
  },
  {
    header: 'Свойство',
    field: 'typeName',
    sortable: true,
  },
  {
    header: 'Описание',
    field: 'desc',
  },
  {
    header: 'Путь',
    field: 'path',
    sortable: true,
  },
  {
    header: 'Сист. имя',
    field: 'sysName',
    sortable: true,
  },
  {
    header: 'Права',
    field: 'rules',
  },
  {
    header: 'Значение',
    field: 'value',
    sortable: true,
    bodyStyle: 'text-align: right;',
  },
  {
    header: 'Ед. изм.',
    field: 'unitShortName',
  },
  {
    header: 'Расч знач',
    field: 'outputValue',
    sortable: true,
    bodyStyle: 'text-align: right;',
  },
  {
    header: 'Качество',
    field: 'quality',
  },
  {
    header: 'Интерпретация',
    field: 'outputValueText',
  },
  {
    header: 'Дата обновления',
    field: 'date',
  },
  {
    header: 'Отобраз',
    field: 'isVisibleForm',
    dataType: 'check-box',
    headerStyle: 'width: 1rem',
    bodyStyle: 'text-align: center;overflow: visible',
    configComponent: {
      name: 'i',
      classTrue: 'pi true-icon pi-check-circle',
      classFalse: 'pi false-icon pi-times-circle',
      componentFunction: () => { },
    },
  },
  {
    header: 'Событие',
    field: 'event',
  },
]);

const columnsFit = computed(() => {
  return columns.value.filter((item) => item.visible);
});

const propertyFit = computed(() => {
  const source = isSecondPropertyShow.value ? props.secondProperty : props.property;
  return source.map((item) => ({
    ...item,
    pid: item.id,
    quality: prepareQuality(item.quality),
    outputValue: prepareValue(item.outputValue),
    value: prepareValue(item.value),
    date: item.dateProperty
      ? store.getters.typeFormat(new Date(item.dateProperty), 'date-time')
      : null,
    event: prepareEvent(item.id, item.flangeNo),
  }));
});

const prepareQuality = (qualityId) => {
  let quality = '';
  store.getters.quality.forEach((element) => {
    if (element.quality_value_id === qualityId) {
      quality = element.quality_value_name;
    }
  });
  return quality;
};

const prepareValue = (valueObj) => {
  if (!valueObj || valueObj.value === undefined) return null;
  if (valueObj.valueClass === 'java.lang.Boolean') return Boolean(valueObj.value);
  return valueObj?.value % 1 === 0 ? valueObj.value : valueObj.value.toFixed(3);
};

const prepareEvent = (propertyId, flangeNo) => {
  for (const item of store.getters.events) {
    if (item.propertyId === propertyId && item.flangeNo === flangeNo) {
      return `${item.eventType.name}; ${item.date ? store.getters.typeFormat(new Date(item.date), 'date-time') : ''}`;
    }
  }
  return '';
};

const prepareColumns = () => {
  const visibleColumns = store.getters.visibleColumns ?? {};
  currentColumns.value = visibleColumns[props.tableName]
    ? visibleColumns[props.tableName]
    : [...props.columnVisible];

  columns.value = columns.value.map((item) => ({
    ...item,
    fieldHeader: item.field + item.header,
    visible: currentColumns.value.includes(item.field),
  }));
};

const onDblClick = () => {
  if (store.getters.info.isAdmin || store.getters.info.isControl) {
    const val = selection.value.value;
    switch (val) {
      case undefined:
        propertyValue.value = null;
        break;
      case true:
      case false:
        propertyValue.value = Number(val);
        break;
      default:
        propertyValue.value = val;
        break;
    }
    showWriteDialog.value = true;
  }
};

const writeValue = () => {
  const properties = store.getters.propertyAll;
  const systemProp = prepareURIData(properties, 'sysName', 'flangeNo', '');
  propertyWrite(
    systemProp,
    properties,
    selection.value.sysName,
    propertyValue.value,
    selection.value.flangeNo
  );
  showWriteDialog.value = false;
};

const changeColumn = (event) => {
  const cols = [...columns.value];
  columns.value.forEach((column) => {
    const index = event.value.findIndex((item) => item === column.field);
    column.visible = index > -1;
  });

  store.dispatch('setVisibleColumns', { table: props.tableName, columns: event.value });
  columns.value = cols;
};

// === Lifecycle ===
onMounted(() => {
  prepareColumns();

  qualityService.value = store.getters.nameToService(
    'BaseService',
    store.getters.info.login,
    store.getters.password,
    'quality_value',
    'ref'
  );

  if (!store.getters.quality.length) {
    qualityService.value.getData().then((data) => {
      store.dispatch('setQuality', data);
    });
  }
});
</script>

<style scoped lang="scss">
.minimize {
  max-width: 35px;
}

::v-deep(.p-datatable.p-datatable-sm .p-datatable-header) {
  padding: 0.1rem 0.1rem;
}
</style>